import React, { useEffect, useMemo } from 'react';

const personFieldNames = {
  displayName: 'personDisplayName',
  nameKana: 'personNameKana',
  email: 'personEmail',
  phone: 'personPhone',
  organization: 'personOrganization',
  roleTitle: 'personRoleTitle',
  internalNote: 'personInternalNote',
  isActive: 'personIsActive',
};

const getPersonUsageLabel = (person) => {
  const summary = person?.usageSummary || {};
  const labels = [
    summary.instructorsCount ? `講師 ${summary.instructorsCount}` : '',
    summary.organizersCount ? `主催者 ${summary.organizersCount}` : '',
    summary.blogAuthorsCount ? `ブログ執筆者 ${summary.blogAuthorsCount}` : '',
  ].filter(Boolean);

  return labels.join(' / ');
};

const AdminPersonLedgerSection = ({
  content,
  register,
  errors = {},
  watch,
  setValue,
  personOptions = [],
  currentPerson = null,
  isLoadingOptions = false,
  optionLoadError = '',
}) => {
  const selectedPersonId = watch ? watch('personId') : '';
  const selectedPerson = useMemo(
    () => {
      if (!watch) {
        return currentPerson || null;
      }

      if (!selectedPersonId) {
        return null;
      }

      return personOptions.find((option) => option.value === selectedPersonId)?.person ||
        (selectedPersonId === currentPerson?.id ? currentPerson : null);
    },
    [currentPerson, personOptions, selectedPersonId, watch],
  );
  const ledgerContent = content?.form?.personLedger || {};
  const labels = ledgerContent.labels || {};
  const relatedRoles = selectedPerson?.relatedRoles || [];
  const usageLabel = getPersonUsageLabel(selectedPerson);

  useEffect(() => {
    if (!setValue) {
      return;
    }

    if (!selectedPersonId) {
      setValue(personFieldNames.displayName, '');
      setValue(personFieldNames.nameKana, '');
      setValue(personFieldNames.email, '');
      setValue(personFieldNames.phone, '');
      setValue(personFieldNames.organization, '');
      setValue(personFieldNames.roleTitle, '');
      setValue(personFieldNames.internalNote, '');
      setValue(personFieldNames.isActive, true);
      return;
    }

    if (!selectedPerson) {
      return;
    }

    setValue(personFieldNames.displayName, selectedPerson.displayName || '');
    setValue(personFieldNames.nameKana, selectedPerson.nameKana || '');
    setValue(personFieldNames.email, selectedPerson.email || '');
    setValue(personFieldNames.phone, selectedPerson.phone || '');
    setValue(personFieldNames.organization, selectedPerson.organization || '');
    setValue(personFieldNames.roleTitle, selectedPerson.roleTitle || '');
    setValue(personFieldNames.internalNote, selectedPerson.internalNote || '');
    setValue(personFieldNames.isActive, selectedPerson.isActive !== false);
  }, [selectedPerson, selectedPersonId, setValue]);

  return (
    <section className="yowan-admin-detail-panel">
      <header className="yowan-admin-event-detail__section-header">
        <h2 className="yowan-admin-detail-panel__title">{ledgerContent.title || '人物台帳'}</h2>
        <p className="yowan-admin-detail-panel__lead">
          {ledgerContent.lead || '会員プロフィールや公開プロフィールとは別に、運営が関係者情報を整理するための内部記録です。'}
        </p>
      </header>

      {isLoadingOptions ? (
        <p className="yowan-admin-form__field-note">{ledgerContent.loadingText || '人物台帳の選択肢を読み込んでいます。'}</p>
      ) : null}
      {optionLoadError ? (
        <p className="yowan-admin-form__message">{optionLoadError}</p>
      ) : null}

      <label className="c-form__field">
        <span className="c-form__label">{labels.personId || '既存人物台帳'}</span>
        <select className="c-form__control" {...register('personId')}>
          <option value="">{ledgerContent.noPersonOptionLabel || '紐づけなし'}</option>
          {personOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
          {ledgerContent.personSelectHelpText || '既存人物台帳を選ぶと、この役割プロフィールと内部人物台帳を明示的に紐づけます。'}
        </p>
      </label>

      <div className="yowan-admin-event-detail__grid">
        <label className="c-form__field">
          <span className="c-form__label">{labels.displayName || 'お名前'}</span>
          <input
            className="c-form__control"
            type="text"
            {...register(personFieldNames.displayName)}
          />
          {errors.personDisplayName ? <em className="yowan-admin-form__error">{errors.personDisplayName.message}</em> : null}
        </label>
        <label className="c-form__field">
          <span className="c-form__label">{labels.nameKana || 'ふりがな'}</span>
          <input className="c-form__control" type="text" {...register(personFieldNames.nameKana)} />
        </label>
        <label className="c-form__field">
          <span className="c-form__label">{labels.email || 'メールアドレス'}</span>
          <input className="c-form__control" type="email" {...register(personFieldNames.email)} />
        </label>
        <label className="c-form__field">
          <span className="c-form__label">{labels.phone || '電話番号'}</span>
          <input className="c-form__control" type="text" {...register(personFieldNames.phone)} />
        </label>
        <label className="c-form__field">
          <span className="c-form__label">{labels.organization || '所属・団体'}</span>
          <input className="c-form__control" type="text" {...register(personFieldNames.organization)} />
        </label>
        <label className="c-form__field">
          <span className="c-form__label">{labels.roleTitle || '肩書き'}</span>
          <input className="c-form__control" type="text" {...register(personFieldNames.roleTitle)} />
        </label>
      </div>

      <label className="c-form__field">
        <span className="c-form__label">{labels.internalNote || '管理メモ'}</span>
        <textarea className="c-form__control" rows="4" {...register(personFieldNames.internalNote)} />
        <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
          {ledgerContent.memberBoundaryHelpText || '会員プロフィールとは独立した内部記録です。双方の情報は自動同期されません。'}
        </p>
      </label>

      <label className="c-form__field c-form__field--checkbox">
        <span className="c-form__label">{labels.isActive || '人物台帳を有効にする'}</span>
        <input type="checkbox" {...register(personFieldNames.isActive)} />
      </label>

      {selectedPerson ? (
        <div className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
          <p>{usageLabel || ledgerContent.noUsageText || 'この人物台帳の紐づきはまだありません。'}</p>
          {relatedRoles.length ? (
            <ul>
              {relatedRoles.map((role) => (
                <li key={`${role.role}-${role.id}`}>
                  {role.detailHref ? (
                    <a href={role.detailHref}>{role.label || role.role || role.id}</a>
                  ) : (
                    <span>{role.label || role.role || role.id}</span>
                  )}
                  {role.isActive === false ? '（停止中）' : ''}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {selectedPerson?.usageSummary?.totalCount > 1 ? (
        <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
          {ledgerContent.sharedHelpText || 'この人物台帳は他の役割にも紐づいています。内部人物情報の変更は共有されます。'}
        </p>
      ) : null}
    </section>
  );
};

export default AdminPersonLedgerSection;
